import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  MdChevronLeft,
  MdLocationOn,
  MdBed,
  MdBathtub,
  MdStraighten,
  MdLocalParking,
  MdCheckCircle,
  MdCall,
  MdEmail,
  MdHomeWork,
  MdApartment,
  MdOutlinePhoto,
  MdChevronRight,
  MdClose,
  MdTune,
  MdChatBubbleOutline,
  MdShare,
  MdFlag,
} from 'react-icons/md';
import { FaStar } from 'react-icons/fa';
import './PropertyDetails.css';
import {
  createBnbBooking,
  getPublicBnbUnitById,
  getPublicPropertyById,
  reconcilePublicPropertyUnits,
  getPropertyRatingsPublic,
  requestUnit,
  getPropertyUnitTenancyHistory,
  uploadPaymentEvidencePhoto,
  submitBnbBookingPaymentEvidence,
  getMyUnitRequests,
  getMyBnbBookings,
  generateUnitShareLink,
  generatePropertyShareLink,
} from '../../../api/services/propertyService';
import ReportModal from '../../shared/ReportModal/ReportModal';
import PropertyMap from '../../../components/PropertyMap/PropertyMap';
import { getAuthToken } from '../../../api/services/authService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { getOrCreateTenantLandlordThread, getOrCreateBnbHostThread } from '../../../api/services/chatService';
import {
  filterUnitsByListingScope,
  isBnbUnit as isPublicBnbUnit,
  isUnitListedPublicly,
  normalizeListingScope,
  resolveListingScope,
} from '../../../utils/publicListingScope';
import { isUnitNotFoundError } from '../../../utils/publicUnitValidation';

const FALLBACK_COORDS = { lat: -1.2921, lng: 36.8219 };

function formatMoneyKES(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return `KES ${amount.toLocaleString()}`;
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getDefaultStayDates() {
  const today = new Date();
  const checkInDate = new Date(today);
  const checkOutDate = new Date(today);
  checkOutDate.setDate(checkOutDate.getDate() + 1);
  return {
    checkIn: toIsoDate(checkInDate),
    checkOut: toIsoDate(checkOutDate),
  };
}

function addDays(isoDate, days) {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

function isBnbDateRangeUnavailable(data) {
  return data?.isAvailableForDates === false || data?.isAvailable === false;
}

function parseNextAvailableDate(data) {
  const raw =
    data?.nextAvailableDate ??
    data?.nextAvailableCheckInDate ??
    data?.nextAvailableCheckIn ??
    data?.earliestAvailableDate ??
    data?.availability?.nextAvailableDate;
  if (!raw) return null;
  const str = String(raw).trim();
  return str.length >= 10 ? str.slice(0, 10) : null;
}

function formatDisplayDate(isoDate) {
  if (!isoDate) return '';
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getNights(start, end) {
  if (!start || !end) return 0;
  // Basic date parsing handles YYYY-MM-DD from standard HTML date inputs
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) {
    // Attempt custom parsing if standard fails (e.g., if somehow DD/MM/YYYY is passed)
    const tryS = new Date(start.split('/').reverse().join('-'));
    const tryE = new Date(end.split('/').reverse().join('-'));
    if (isNaN(tryS.getTime()) || isNaN(tryE.getTime())) return 0;
    const diff = tryE.getTime() - tryS.getTime();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 3600 * 24));
  }
  const diff = e.getTime() - s.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 3600 * 24));
}

function isBnbUnit(unit) {
  return isPublicBnbUnit(unit);
}

function unitHasAssignedTenant(unit) {
  if (!unit) return false;
  const name = String(unit.tenantName ?? '').trim();
  const id = String(unit.tenantId ?? '').trim();
  if (name || id) return true;
  const nested = unit.tenant || unit.assignedUser || unit.user;
  return Boolean(nested && typeof nested === 'object' && (nested.id || nested.userId));
}

function formatLocation(location, address) {
  const area = location?.area || '';
  const city = location?.city || '';
  const label = [area, city].filter(Boolean).join(', ');
  return label || address || 'Location not specified';
}

function pickImages(property) {
  const images = Array.isArray(property?.imageUrls) ? property.imageUrls.filter(Boolean) : [];
  if (images.length > 0) return images;
  if (property?.imageUrl) return [property.imageUrl];
  return [];
}

function getCoverImageForUnit(unit) {
  const photos = Array.isArray(unit?.photos) ? unit.photos.filter(Boolean) : [];
  return photos[0] || null;
}

function parseRatingValue(row) {
  const raw = row?.avgRating ?? row?.averageRating ?? row?.averageScore ?? row?.rating ?? row?.propertyAverageScore ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function parseRatingCount(row) {
  const raw = row?.ratingCount ?? row?.totalRatings ?? row?.reviewsCount ?? row?.propertyRatingCount ?? 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function formatRatingDate(value) {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const PropertyDetails = ({ teaserMode = false }) => {
  const { id: routeParamId } = useParams();
  const location = useLocation();
  const propertyId = useMemo(() => {
    if (routeParamId) return routeParamId;
    // Fallback: TenantDashboard renders this component without a matching Route,
    // so useParams() may be empty. Extract from /tenant/property/:id.
    const match = location?.pathname?.match(/\/tenant\/property\/([^/]+)/);
    return match?.[1] || '';
  }, [routeParamId, location?.pathname]);
  const navigate = useNavigate();
  const token = getAuthToken();

  /** Redirect to login if unauthenticated, saving current path for return */
  const ensureAuth = () => {
    if (!token) {
      navigate('/login', { state: { from: location } });
      return false;
    }
    return true;
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [property, setProperty] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialUnitIdFromUrl = queryParams.get('unitId');
  const listingScope = useMemo(() => {
    const fromQuery = normalizeListingScope(
      queryParams.get('scope') || queryParams.get('dashboardScope') || queryParams.get('type')
    );
    return fromQuery;
  }, [queryParams]);

  const [selectedUnitId, setSelectedUnitId] = useState(initialUnitIdFromUrl);
  const [propertyImageSrcs, setPropertyImageSrcs] = useState([]);
  const [unitCoverSrcById, setUnitCoverSrcById] = useState({});
  const [unitCoverLoadingById, setUnitCoverLoadingById] = useState({});
  const [unitCoverErrorById, setUnitCoverErrorById] = useState({});
  const [sortBy, setSortBy] = useState('rentAsc'); // rentAsc | rentDesc | bedsDesc | sizeDesc
  const [availabilityFilter, setAvailabilityFilter] = useState('all'); // all | available
  const [bedroomsFilter, setBedroomsFilter] = useState('any'); // any | 0 | 1 | 2 | 3 | 4
  const [selectedUnitImageSrcs, setSelectedUnitImageSrcs] = useState([]);
  const [selectedUnitImageIndex, setSelectedUnitImageIndex] = useState(0);
  const [selectedUnitImageErrorByIndex, setSelectedUnitImageErrorByIndex] = useState({});
  const [shareFeedback, setShareFeedback] = useState('');
  const [propertyShareFeedback, setPropertyShareFeedback] = useState('');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [bookingCheckInDate, setBookingCheckInDate] = useState('');
  const [bookingCheckOutDate, setBookingCheckOutDate] = useState('');
  const [bnbDetailsLoading, setBnbDetailsLoading] = useState(false);
  const [bnbAvailabilityChecking, setBnbAvailabilityChecking] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState('');
  const [nextAvailableLoading, setNextAvailableLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportSuccessMessage, setReportSuccessMessage] = useState('');
  const [isRatingsModalOpen, setIsRatingsModalOpen] = useState(false);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [ratingsError, setRatingsError] = useState('');
  const [propertyRatingsPayload, setPropertyRatingsPayload] = useState(null);

  const [showBnbEvidenceStep, setShowBnbEvidenceStep] = useState(false);

  const [bnbMpesaMessage, setBnbMpesaMessage] = useState('');
  const [bnbAmount, setBnbAmount] = useState('');
  const [bnbPhotoUrl, setBnbPhotoUrl] = useState('');
  const [bnbPhotoUploading, setBnbPhotoUploading] = useState(false);
  const [bnbPhotoUploadError, setBnbPhotoUploadError] = useState('');

  const [activeTenancyUnitIds, setActiveTenancyUnitIds] = useState(new Set());
  const [bnbUnitDetailsById, setBnbUnitDetailsById] = useState({});
  const [myRequests, setMyRequests] = useState([]);
  const canViewUnitAnalytics = useMemo(() => {
    try {
      const token = getAuthToken();
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1] || '')) || {};
      const roles = Array.isArray(payload?.roles) ? payload.roles.map((r) => String(r).toUpperCase()) : [];
      return roles.includes('LANDLORD') || roles.includes('AGENT');
    } catch {
      return false;
    }
  }, []);


  const touchStartXRef = useRef(null);
  const touchDeltaXRef = useRef(0);
  const mapSectionRef = useRef(null);

  const stayDates = useMemo(() => {
    const defaults = getDefaultStayDates();
    try {
      const params = new URLSearchParams(location?.search || '');
      const checkIn = params.get('checkIn') || defaults.checkIn;
      const checkOut = params.get('checkOut') || defaults.checkOut;
      return { checkIn, checkOut };
    } catch (_) {
      return defaults;
    }
  }, [location?.search]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!propertyId) {
        setLoading(false);
        setError('Missing property id in URL.');
        return;
      }
      try {
        setLoading(true);
        setError('');
        setProperty(null);
        setSelectedUnitId(initialUnitIdFromUrl);
        setCurrentImageIndex(0);

        const response = await getPublicPropertyById(propertyId, true, {
          dashboardScope: listingScope || undefined,
        });
        const payload = response?.data || response;
        let data = payload?.data || payload;

        if (data && Array.isArray(data.units)) {
          let effectiveScope = resolveListingScope(listingScope, {
            property: data,
            units: data.units,
          });
          if (!listingScope && initialUnitIdFromUrl) {
            const linkedUnit = data.units.find(
              (unit) => String(unit?.id) === String(initialUnitIdFromUrl)
            );
            if (linkedUnit && isBnbUnit(linkedUnit)) {
              effectiveScope = 'BNB';
            }
          }
          let scopedUnits = filterUnitsByListingScope(data.units, effectiveScope).filter(
            isUnitListedPublicly
          );
          scopedUnits = await reconcilePublicPropertyUnits(propertyId, scopedUnits, effectiveScope);
          data = {
            ...data,
            units: scopedUnits,
          };
        }

        if (!cancelled) {
          setProperty(data);
          
          // Update SEO/Meta tags dynamically for browser tabs and smart crawlers
          if (data?.name) {
            document.title = `${data.name} | TenantScore`;
            
            // Try to update meta tags (works for some crawlers and browser previews)
            const metaTitle = document.querySelector('meta[property="og:title"]');
            if (metaTitle) metaTitle.setAttribute('content', `Check out ${data.name}`);
            
            const metaDesc = document.querySelector('meta[property="og:description"]');
            if (metaDesc) metaDesc.setAttribute('content', data.description || 'View this property on TenantScore');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load property.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, listingScope, initialUnitIdFromUrl]);

  const images = useMemo(() => pickImages(property), [property]);
  const units = useMemo(() => (Array.isArray(property?.units) ? property.units : []), [property]);
  const isBnbProperty = useMemo(() => {
    if (listingScope === 'BNB') return true;
    if (listingScope === 'RENTAL') return false;
    if (property?.dashboardScope === 'BNB') return true;
    if (units.length > 0) return isBnbUnit(units[0]);
    return false;
  }, [property, units, listingScope]);
  const availableUnits = useMemo(() => units.filter(isUnitListedPublicly), [units]);

  const isUnitBookable = useCallback((unit) => {
    if (!unit) return false;
    if (unit.isAvailable === false) return false;
    if (unitHasAssignedTenant(unit)) return false;
    if (activeTenancyUnitIds.has(unit.id)) return false;

    // Check if user already has an active request for this unit
    const activeStatus = ['REQUESTED', 'PENDING', 'APPROVED', 'CONFIRMED', 'ASSIGNED', 'CHECKED_IN'];
    const hasActiveRequest = myRequests.some(req => {
      const unitId = req.unitId || req.unitID || req.unit?.id || req.propertyUnitId;
      return String(unitId) === String(unit.id) && activeStatus.includes(String(req.status || '').toUpperCase());
    });

    if (hasActiveRequest) return false;

    return true;
  }, [activeTenancyUnitIds, myRequests]);

  /** Derived from listed units — matches green "Available" badges (not stale API vacantUnits). */
  const vacantUnitCount = useMemo(() => {
    if (isBnbProperty) return 0;
    return availableUnits.filter((u) => isUnitBookable(u)).length;
  }, [availableUnits, isUnitBookable, isBnbProperty]);

  const visibleUnits = useMemo(() => {
    let list = [...availableUnits];

    if (availabilityFilter === 'available') {
      list = list.filter((u) => isUnitBookable(u));
    }

    if (bedroomsFilter !== 'any') {
      const bedCount = Number(bedroomsFilter);
      list = list.filter((u) => Number(u?.bedrooms ?? -999) === bedCount);
    }

    switch (sortBy) {
      case 'rentDesc':
        list.sort((a, b) => {
          const aPrice = isBnbProperty
            ? Number(a?.pricePerNight ?? -1)
            : Number(a?.monthlyRent ?? -1);
          const bPrice = isBnbProperty
            ? Number(b?.pricePerNight ?? -1)
            : Number(b?.monthlyRent ?? -1);
          return bPrice - aPrice;
        });
        break;
      case 'bedsDesc':
        list.sort((a, b) => (Number(b?.bedrooms ?? -1) - Number(a?.bedrooms ?? -1)));
        break;
      case 'sizeDesc':
        list.sort((a, b) => (Number(b?.sizeSqm ?? -1) - Number(a?.sizeSqm ?? -1)));
        break;
      case 'rentAsc':
      default:
        list.sort((a, b) => {
          const aPrice = isBnbProperty
            ? Number(a?.pricePerNight ?? Number.POSITIVE_INFINITY)
            : Number(a?.monthlyRent ?? Number.POSITIVE_INFINITY);
          const bPrice = isBnbProperty
            ? Number(b?.pricePerNight ?? Number.POSITIVE_INFINITY)
            : Number(b?.monthlyRent ?? Number.POSITIVE_INFINITY);
          return aPrice - bPrice;
        });
        break;
    }

    return list;
  }, [availableUnits, availabilityFilter, bedroomsFilter, sortBy, isUnitBookable, isBnbProperty]);

  const selectedUnit = useMemo(
    () => availableUnits.find((u) => u.id === selectedUnitId) || null,
    [availableUnits, selectedUnitId]
  );

  const selectedUnitMerged = useMemo(() => {
    if (!selectedUnit) return null;
    const extra = bnbUnitDetailsById?.[selectedUnit.id];
    if (!extra) return selectedUnit;
    return { ...selectedUnit, ...extra };
  }, [selectedUnit, bnbUnitDetailsById]);

  const selectedBnbDetails = useMemo(() => {
    if (!selectedUnit?.id) return null;
    return bnbUnitDetailsById?.[selectedUnit.id] || null;
  }, [selectedUnit?.id, bnbUnitDetailsById]);

  const isSelectedStayUnavailable = useMemo(() => {
    if (!selectedUnit || !isBnbUnit(selectedUnit)) return false;
    if (!selectedBnbDetails) return false;
    if (
      selectedBnbDetails._fetchedCheckIn !== bookingCheckInDate ||
      selectedBnbDetails._fetchedCheckOut !== bookingCheckOutDate
    ) {
      return false;
    }
    return isBnbDateRangeUnavailable(selectedBnbDetails);
  }, [selectedUnit, selectedBnbDetails, bookingCheckInDate, bookingCheckOutDate]);

  const updateUnitIdInUrl = useCallback((unitId) => {
    const params = new URLSearchParams(location.search || '');
    if (unitId) params.set('unitId', String(unitId));
    else params.delete('unitId');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const handleOpenUnit = useCallback((unitId) => {
    setSelectedUnitId(unitId);
    setIsDrawerOpen(true);
    updateUnitIdInUrl(unitId);
  }, [updateUnitIdInUrl]);

  const handleCloseUnitDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    updateUnitIdInUrl(null);
  }, [updateUnitIdInUrl]);

  const handleShareUnit = useCallback(async () => {
    if (!propertyId || !selectedUnitMerged?.id || typeof window === 'undefined') return;

    try {
      setShareFeedback('Generating...');
      const res = await generateUnitShareLink(propertyId, selectedUnitMerged.id);
      const shareData = res?.data || res;

      if (!shareData?.shareUrl) {
        throw new Error('No share URL returned');
      }

      const fullUrl = shareData.shareUrl.startsWith('http')
        ? shareData.shareUrl
        : `${window.location.origin}${shareData.shareUrl}`;

      if (navigator.share) {
        await navigator.share({ url: fullUrl });
        setShareFeedback('Shared');
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const fallbackInput = document.createElement('textarea');
        fallbackInput.value = fullUrl;
        fallbackInput.setAttribute('readonly', '');
        fallbackInput.style.position = 'absolute';
        fallbackInput.style.left = '-9999px';
        document.body.appendChild(fallbackInput);
        fallbackInput.select();
        document.execCommand('copy');
        document.body.removeChild(fallbackInput);
      }
      setShareFeedback('Link copied');
    } catch (e) {
      console.error('Share unit error:', e);
      setShareFeedback('Unable to share');
    }
  }, [propertyId, selectedUnitMerged]);

  const handleShareProperty = useCallback(async () => {
    if (!propertyId || typeof window === 'undefined') return;

    try {
      setPropertyShareFeedback('Generating...');
      const res = await generatePropertyShareLink(propertyId);
      const shareData = res?.data || res;

      if (!shareData?.shareUrl) {
        throw new Error('No share URL returned');
      }

      const fullUrl = shareData.shareUrl.startsWith('http')
        ? shareData.shareUrl
        : `${window.location.origin}${shareData.shareUrl}`;

      if (navigator.share) {
        await navigator.share({ url: fullUrl });
        setPropertyShareFeedback('Shared');
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const fallbackInput = document.createElement('textarea');
        fallbackInput.value = fullUrl;
        fallbackInput.setAttribute('readonly', '');
        fallbackInput.style.position = 'absolute';
        fallbackInput.style.left = '-9999px';
        document.body.appendChild(fallbackInput);
        fallbackInput.select();
        document.execCommand('copy');
        document.body.removeChild(fallbackInput);
      }
      setPropertyShareFeedback('Link copied');
    } catch (e) {
      console.error('Share property error:', e);
      setPropertyShareFeedback('Unable to share');
    }
  }, [propertyId]);

  const locationLabel = useMemo(
    () => formatLocation(property?.location, property?.address),
    [property?.location, property?.address]
  );

  useEffect(() => {
    let cancelled = false;

    async function preloadPropertyImages() {
      if (!images || images.length === 0) {
        setPropertyImageSrcs([]);
        return;
      }

      // Keep carousel shape stable while authenticated image URLs are being resolved.
      setPropertyImageSrcs(Array(images.length).fill(null));
      const resolved = await Promise.all(
        images.map(async (url) => (await fetchAuthenticatedImage(url)) || url)
      );
      if (!cancelled) {
        setPropertyImageSrcs(resolved);
      }
    }

    preloadPropertyImages();
    return () => {
      cancelled = true;
    };
  }, [images]);

  useEffect(() => {
    let cancelled = false;

    async function loadCover(unit) {
      const cover = getCoverImageForUnit(unit);
      if (!cover) return [unit.id, null];
      try {
        const blobUrl = await fetchAuthenticatedImage(cover);
        return [unit.id, blobUrl || cover];
      } catch {
        return [unit.id, cover];
      }
    }

    async function preloadUnitCovers() {
      if (!availableUnits || availableUnits.length === 0) {
        setUnitCoverSrcById({});
        setUnitCoverLoadingById({});
        setUnitCoverErrorById({});
        return;
      }

      setUnitCoverSrcById({});
      setUnitCoverLoadingById(
        Object.fromEntries(
          availableUnits.map((unit) => [unit.id, Boolean(getCoverImageForUnit(unit))])
        )
      );
      setUnitCoverErrorById({});

      const PRIORITY = 8;
      const priority = availableUnits.slice(0, PRIORITY);
      const rest = availableUnits.slice(PRIORITY);

      const priorityEntries = await Promise.all(priority.map(loadCover));
      if (!cancelled) {
        const next = Object.fromEntries(priorityEntries);
        setUnitCoverSrcById(next);
        setUnitCoverLoadingById((prev) => {
          const updated = { ...prev };
          priorityEntries.forEach(([unitId]) => {
            updated[unitId] = false;
          });
          return updated;
        });
      }

      if (rest.length === 0) return;
      const restEntries = await Promise.all(rest.map(loadCover));
      if (!cancelled) {
        setUnitCoverSrcById((prev) => ({ ...prev, ...Object.fromEntries(restEntries) }));
        setUnitCoverLoadingById((prev) => {
          const updated = { ...prev };
          restEntries.forEach(([unitId]) => {
            updated[unitId] = false;
          });
          return updated;
        });
      }
    }

    preloadUnitCovers();
    return () => {
      cancelled = true;
    };
  }, [availableUnits]);

  useEffect(() => {
    let cancelled = false;

    async function loadActiveTenancyStatus() {
      // This endpoint is restricted to property owners and assigned agents.
      if (!canViewUnitAnalytics) {
        setActiveTenancyUnitIds(new Set());
        return;
      }
      if (!propertyId || !availableUnits || availableUnits.length === 0) {
        setActiveTenancyUnitIds(new Set());
        return;
      }

      const checks = await Promise.allSettled(
        availableUnits.map(async (unit) => {
          if (!unit?.id) return null;
          try {
            const response = await getPropertyUnitTenancyHistory(propertyId, unit.id);
            const rows = Array.isArray(response?.data) ? response.data : [];
            const hasActiveTenancy = rows.some((row) => String(row?.status || '').toUpperCase() === 'ACTIVE');
            return hasActiveTenancy ? { occupiedId: unit.id } : null;
          } catch (error) {
            if (isUnitNotFoundError(error)) {
              return { removedId: unit.id };
            }
            return null;
          }
        })
      );

      if (cancelled) return;

      const occupiedIds = new Set();
      const removedIds = new Set();
      checks.forEach((result) => {
        if (result.status !== 'fulfilled' || !result.value) return;
        if (result.value.removedId) {
          removedIds.add(result.value.removedId);
        } else if (result.value.occupiedId) {
          occupiedIds.add(result.value.occupiedId);
        }
      });
      setActiveTenancyUnitIds(occupiedIds);
      if (removedIds.size > 0) {
        setProperty((prev) => {
          if (!prev) return prev;
          const nextUnits = (prev.units || []).filter(
            (u) => !removedIds.has(String(u?.id ?? ''))
          );
          return { ...prev, units: nextUnits };
        });
        setSelectedUnitId((current) => (removedIds.has(String(current || '')) ? null : current));
      }
    }

    loadActiveTenancyStatus();
    return () => {
      cancelled = true;
    };
  }, [propertyId, availableUnits, canViewUnitAnalytics]);

  useEffect(() => {
    let cancelled = false;
    async function loadMyRequests() {
      if (!token) {
        setMyRequests([]);
        return;
      }
      try {
        const [rentalRes, bnbRes] = await Promise.allSettled([
          getMyUnitRequests(),
          getMyBnbBookings()
        ]);
        if (cancelled) return;

        const rentalList = rentalRes.status === 'fulfilled' ? (rentalRes.value?.data || rentalRes.value || []) : [];
        const bnbList = bnbRes.status === 'fulfilled' ? (bnbRes.value?.data || bnbRes.value || []) : [];

        const merged = [
          ...Array.isArray(rentalList) ? rentalList : [],
          ...Array.isArray(bnbList) ? bnbList : []
        ];
        setMyRequests(merged);
      } catch (e) {
        console.error('Failed to load my requests:', e);
      }
    }
    loadMyRequests();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    let cancelled = false;

    async function preloadSelectedUnitImages() {
      const photos = Array.isArray(selectedUnitMerged?.photos)
        ? selectedUnitMerged.photos.filter(Boolean).slice(0, 8)
        : [];
      if (!selectedUnitMerged || photos.length === 0) {
        setSelectedUnitImageSrcs([]);
        setSelectedUnitImageIndex(0);
        setSelectedUnitImageErrorByIndex({});
        return;
      }

      setSelectedUnitImageSrcs(Array(photos.length).fill(null));
      setSelectedUnitImageIndex(0);
      setSelectedUnitImageErrorByIndex({});
      const resolved = await Promise.all(
        photos.map(async (url) => (await fetchAuthenticatedImage(url)) || url)
      );
      if (!cancelled) {
        setSelectedUnitImageSrcs(resolved);
      }
    }

    preloadSelectedUnitImages();
    return () => {
      cancelled = true;
    };
  }, [selectedUnitMerged]);

  useEffect(() => {
    const unitIdFromUrl = queryParams.get('unitId');
    setSelectedUnitId(unitIdFromUrl || null);
    if (!unitIdFromUrl) {
      setIsDrawerOpen(false);
    }
  }, [queryParams]);

  useEffect(() => {
    if (selectedUnitId && selectedUnit && !isDrawerOpen) {
      setIsDrawerOpen(true);
    }
  }, [selectedUnitId, selectedUnit, isDrawerOpen]);

  useEffect(() => {
    setRequestNotes('');
    setRequestError('');
    setRequestSuccess(false);
    setBookingCheckInDate(stayDates.checkIn);
    setBookingCheckOutDate(stayDates.checkOut);
    setNextAvailableDate('');
    setNextAvailableLoading(false);
    setBnbMpesaMessage('');
    setBnbAmount('');
    setShowBnbEvidenceStep(false);
    setBnbPhotoUrl('');
    setBnbPhotoUploadError('');
  }, [selectedUnitId, stayDates.checkIn, stayDates.checkOut]);

  useEffect(() => {
    if (!shareFeedback) return undefined;
    const timeout = window.setTimeout(() => setShareFeedback(''), 1800);
    return () => window.clearTimeout(timeout);
  }, [shareFeedback]);

  useEffect(() => {
    if (!propertyShareFeedback) return undefined;
    const timeout = window.setTimeout(() => setPropertyShareFeedback(''), 1800);
    return () => window.clearTimeout(timeout);
  }, [propertyShareFeedback]);

  useEffect(() => {
    setShowBnbEvidenceStep(false);
    setBnbPhotoUrl('');
    setBnbMpesaMessage('');
  }, [bookingCheckInDate, bookingCheckOutDate]);

  useEffect(() => {
    let cancelled = false;
    async function loadBnbUnitDetails() {
      if (!selectedUnit?.id) return;
      if (!isBnbUnit(selectedUnit)) return;
      if (!bookingCheckInDate || !bookingCheckOutDate) return;

      const current = bnbUnitDetailsById?.[selectedUnit.id];
      if (
        current &&
        current._fetchedCheckIn === bookingCheckInDate &&
        current._fetchedCheckOut === bookingCheckOutDate
      ) {
        return;
      }

      try {
        setBnbDetailsLoading(true);
        const dates = { checkIn: bookingCheckInDate, checkOut: bookingCheckOutDate };
        const res = await getPublicBnbUnitById(selectedUnit.id, dates);
        const payload = res?.data || res;
        const data = payload?.data || payload;
        if (!cancelled && data) {
          // If the backend returns a calculated price object or root fields, use them.
          // Prioritize backend fields like pricePerNight, subtotal, and totalAmount.
          const fetchedTotal = data.totalAmount || data.pricing?.totalAmount || data.price?.totalAmount;
          const fetchedRate = data.pricePerNight || data.pricing?.pricePerNight || data.price?.pricePerNight;
          const fetchedSubtotal = data.subtotal || data.pricing?.subtotal || data.price?.subtotal;

          const nights = getNights(bookingCheckInDate, bookingCheckOutDate);
          const price = fetchedRate || data.pricePerNight || selectedUnit.pricePerNight || 0;
          const fallbackTotal = nights * price;

          setBnbUnitDetailsById((prev) => ({
            ...prev,
            [selectedUnit.id]: {
              ...data,
              // Use fetched rate if available, otherwise original
              pricePerNight: fetchedRate || data.pricePerNight || selectedUnit.pricePerNight,
              totalAmount: fetchedTotal ?? (fallbackTotal > 0 ? fallbackTotal : null),
              subtotal: fetchedSubtotal ?? (fetchedTotal || (fallbackTotal > 0 ? fallbackTotal : null)),
              _fetchedCheckIn: bookingCheckInDate,
              _fetchedCheckOut: bookingCheckOutDate
            }
          }));

          const finalTotal = fetchedTotal || fallbackTotal;
          if (finalTotal > 0) {
            setBnbAmount(String(finalTotal));
          }
        }
      } catch (e) {
        // Don’t block the drawer; keep base unit data if details call fails.
      } finally {
        if (!cancelled) setBnbDetailsLoading(false);
      }
    }
    loadBnbUnitDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedUnit?.id, bookingCheckInDate, bookingCheckOutDate, bnbUnitDetailsById, selectedUnit]);

  useEffect(() => {
    let cancelled = false;

    async function findNextAvailableDate() {
      if (!selectedUnit?.id || !isBnbUnit(selectedUnit) || !bookingCheckInDate || !bookingCheckOutDate) {
        setNextAvailableDate('');
        setNextAvailableLoading(false);
        return;
      }

      const details = bnbUnitDetailsById?.[selectedUnit.id];
      const fetchedForCurrentDates =
        details?._fetchedCheckIn === bookingCheckInDate &&
        details?._fetchedCheckOut === bookingCheckOutDate;

      if (!fetchedForCurrentDates || bnbDetailsLoading) {
        return;
      }

      if (!isBnbDateRangeUnavailable(details)) {
        setNextAvailableDate('');
        setNextAvailableLoading(false);
        return;
      }

      const fromApi = parseNextAvailableDate(details);
      if (fromApi && fromApi >= bookingCheckInDate) {
        setNextAvailableDate(fromApi);
        setNextAvailableLoading(false);
        return;
      }

      setNextAvailableLoading(true);
      try {
        let candidate = addDays(bookingCheckInDate, 1);
        for (let attempt = 0; attempt < 60; attempt += 1) {
          if (cancelled) return;
          const candidateCheckOut = addDays(candidate, 1);
          const res = await getPublicBnbUnitById(selectedUnit.id, {
            checkIn: candidate,
            checkOut: candidateCheckOut,
          });
          const payload = res?.data || res;
          const data = payload?.data || payload;
          const apiNext = parseNextAvailableDate(data);
          if (apiNext && apiNext >= candidate) {
            if (!cancelled) setNextAvailableDate(apiNext);
            return;
          }
          if (!isBnbDateRangeUnavailable(data)) {
            if (!cancelled) setNextAvailableDate(candidate);
            return;
          }
          candidate = addDays(candidate, 1);
        }
        if (!cancelled) setNextAvailableDate('');
      } catch (_) {
        if (!cancelled) setNextAvailableDate('');
      } finally {
        if (!cancelled) setNextAvailableLoading(false);
      }
    }

    findNextAvailableDate();
    return () => {
      cancelled = true;
    };
  }, [
    selectedUnit?.id,
    selectedUnit,
    bookingCheckInDate,
    bookingCheckOutDate,
    bnbUnitDetailsById,
    bnbDetailsLoading,
  ]);

  const handleUseNextAvailableDate = useCallback(() => {
    if (!nextAvailableDate) return;
    setBookingCheckInDate(nextAvailableDate);
    setBookingCheckOutDate(addDays(nextAvailableDate, 1));
    setShowBnbEvidenceStep(false);
    setRequestError('');
  }, [nextAvailableDate]);

  const handleRequestUnit = async () => {
    if (!ensureAuth()) return;
    if (!propertyId || !selectedUnit?.id) return;
    if (!isUnitBookable(selectedUnit)) return;
    try {
      setRequestLoading(true);
      setRequestError('');
      setRequestSuccess(false);
      await requestUnit(propertyId, selectedUnit.id, {
        notes: requestNotes.trim() || undefined,
      });
      setRequestSuccess(true);
      setRequestNotes('');
    } catch (e) {
      setRequestError(e?.message || 'Failed to request unit.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleCreateBnbBooking = async () => {
    if (!ensureAuth()) return;
    if (!selectedUnit?.id) return;
    if (!bookingCheckInDate || !bookingCheckOutDate) {
      setRequestError('Please choose check-in and check-out dates.');
      return;
    }
    if (bookingCheckOutDate <= bookingCheckInDate) {
      setRequestError('Check-out date must be after check-in date.');
      return;
    }

    if (!showBnbEvidenceStep) {
      try {
        setBnbAvailabilityChecking(true);
        setRequestError('');
        setRequestSuccess(false);

        const res = await getPublicBnbUnitById(selectedUnit.id, {
          checkIn: bookingCheckInDate,
          checkOut: bookingCheckOutDate,
        });
        const payload = res?.data || res;
        const data = payload?.data || payload;

        if (data?.isAvailableForDates === false || data?.isAvailable === false) {
          setShowBnbEvidenceStep(false);
          setRequestError('This BnB is already booked for the selected dates. Please choose different dates.');
          return;
        }

        const resolvedAmount =
          data?.totalAmount ??
          data?.pricing?.totalAmount ??
          selectedUnitMerged?.totalAmount ??
          (getNights(bookingCheckInDate, bookingCheckOutDate) * (selectedUnitMerged?.pricePerNight || 0));
        if (resolvedAmount > 0) {
          setBnbAmount(String(resolvedAmount));
        }

        setShowBnbEvidenceStep(true);
      } catch (e) {
        setShowBnbEvidenceStep(false);
        setRequestError(e?.message || 'Failed to verify booking availability. Please try again.');
      } finally {
        setBnbAvailabilityChecking(false);
      }
      return;
    }

    if (!bnbMpesaMessage.trim() && !bnbPhotoUrl) {
      setRequestError('Please provide M-Pesa message or upload a receipt photo.');
      return;
    }

    try {
      setRequestLoading(true);
      setRequestError('');
      setRequestSuccess(false);

      const bookingRes = await createBnbBooking({
        unitId: selectedUnit.id,
        checkInDate: bookingCheckInDate,
        checkOutDate: bookingCheckOutDate,
        notes: requestNotes.trim() || undefined,
      });
      const bookingPayload = bookingRes?.data || bookingRes;
      const bookingData = bookingPayload?.data || bookingPayload;
      const bookingId = bookingData?.id;
      if (!bookingId) {
        throw new Error('Could not create your booking request. Please try again.');
      }

      const amount =
        Number(bnbAmount) ||
        bookingData?.totalAmount ||
        selectedUnitMerged?.totalAmount ||
        0;
      await submitBnbBookingPaymentEvidence(bookingId, {
        amount,
        mpesaMessage: bnbMpesaMessage.trim() || undefined,
        photoUrl: bnbPhotoUrl || undefined,
      });

      setRequestSuccess(true);
      setBnbMpesaMessage('');
      setBnbPhotoUrl('');
      setShowBnbEvidenceStep(false);
    } catch (e) {
      const message = String(e?.message || '');
      const normalizedMessage = message.toLowerCase();
      const explicitlyUnavailable =
        normalizedMessage.includes('already booked') ||
        normalizedMessage.includes('not available') ||
        normalizedMessage.includes('unavailable') ||
        normalizedMessage.includes('occupied');

      if (explicitlyUnavailable) {
        setShowBnbEvidenceStep(false);
        setRequestError('This BnB is no longer available for the selected dates. Please check availability again.');
        return;
      }

      setRequestError(e?.message || 'Action failed. Please try again.');
    } finally {
      setRequestLoading(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBnbPhotoUploading(true);
      setBnbPhotoUploadError('');
      const res = await uploadPaymentEvidencePhoto(file);
      const url = res?.data?.url || res?.data || res;
      setBnbPhotoUrl(url);
    } catch (err) {
      setBnbPhotoUploadError(err?.message || 'Failed to upload photo.');
    } finally {
      setBnbPhotoUploading(false);
      e.target.value = '';
    }
  };
  const handleStartPropertyChat = async () => {
    if (!ensureAuth()) return;
    if (!propertyId) return;
    try {
      setRequestLoading(true);

      const isBnb = isBnbUnit(selectedUnitMerged || selectedUnit);
      const thread = isBnb
        ? await getOrCreateBnbHostThread(propertyId)
        : await getOrCreateTenantLandlordThread(propertyId);

      // Navigate to messages with the current thread ID in state
      navigate('/tenant/messages', { state: { threadId: thread.id } });
    } catch (e) {
      setRequestError(e?.message || 'Failed to open conversation.');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1 && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const goPrev = () => {
    const n = propertyImageSrcs.length;
    if (n <= 1) return;
    setCurrentImageIndex((prev) => (prev - 1 + n) % n);
  };

  const goNext = () => {
    const n = propertyImageSrcs.length;
    if (n <= 1) return;
    setCurrentImageIndex((prev) => (prev + 1) % n);
  };

  const onTouchStart = (e) => {
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== 'number') return;
    touchStartXRef.current = x;
    touchDeltaXRef.current = 0;
  };

  const onTouchMove = (e) => {
    const x = e.touches?.[0]?.clientX;
    if (typeof x !== 'number') return;
    if (touchStartXRef.current == null) return;
    touchDeltaXRef.current = x - touchStartXRef.current;
  };

  const onTouchEnd = () => {
    const dx = touchDeltaXRef.current;
    touchStartXRef.current = null;
    touchDeltaXRef.current = 0;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  const contactPhone = property?.contacts?.phone || '';
  const contactEmail = property?.contacts?.email || '';
  const isPropertyContentLoading = loading || (!property && !error);
  const carouselSlides =
    images.length > 0
      ? (propertyImageSrcs.length > 0 ? propertyImageSrcs : Array(images.length).fill(null))
      : [];

  const coords = {
    lat: property?.location?.lat ?? FALLBACK_COORDS.lat,
    lng: property?.location?.lng ?? FALLBACK_COORDS.lng,
  };

  const openMap = () => {
    if (!ensureAuth()) return;
    if (mapSectionRef.current) {
      mapSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const query = encodeURIComponent(property?.address || locationLabel);
    const url = `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}&query_place_id=${query}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const call = () => {
    if (!ensureAuth()) return;
    if (!contactPhone) return;
    window.location.href = `tel:${contactPhone}`;
  };

  const email = () => {
    if (!ensureAuth()) return;
    if (!contactEmail) return;
    const subject = encodeURIComponent(`Inquiry: ${property?.name || 'Property'}`);
    window.location.href = `mailto:${contactEmail}?subject=${subject}`;
  };

  const propertyAverageScore = useMemo(() => parseRatingValue(property), [property]);
  const propertyRatingCount = useMemo(() => parseRatingCount(property), [property]);
  const propertyRatingsList = useMemo(() => {
    const payloadData = propertyRatingsPayload?.data || propertyRatingsPayload || {};
    return Array.isArray(payloadData?.ratings) ? payloadData.ratings : [];
  }, [propertyRatingsPayload]);
  const modalAverageScore = useMemo(() => {
    const payloadData = propertyRatingsPayload?.data || propertyRatingsPayload || {};
    return parseRatingValue(payloadData) || propertyAverageScore;
  }, [propertyRatingsPayload, propertyAverageScore]);
  const modalRatingCount = useMemo(() => {
    const payloadData = propertyRatingsPayload?.data || propertyRatingsPayload || {};
    return parseRatingCount(payloadData) || propertyRatingCount;
  }, [propertyRatingsPayload, propertyRatingCount]);

  const handleOpenRatingsModal = useCallback(async () => {
    if (!propertyId) return;
    setIsRatingsModalOpen(true);
    if (propertyRatingsPayload || ratingsLoading) return;

    try {
      setRatingsLoading(true);
      setRatingsError('');
      const payload = await getPropertyRatingsPublic(propertyId);
      setPropertyRatingsPayload(payload || null);
    } catch (e) {
      setRatingsError(e?.message || 'Failed to load ratings and comments.');
    } finally {
      setRatingsLoading(false);
    }
  }, [propertyId, propertyRatingsPayload, ratingsLoading]);

  return (
    <div className="property-details-page property-details--public">
      {/* Top carousel */}
      <div className="property-appbar">
        <div className="appbar-header">
          <button type="button" className="back-button" onClick={handleBack} aria-label="Go back">
            <MdChevronLeft />
          </button>
          <div />
        </div>

        <div
          className="image-carousel-container"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-label="Property images"
        >
          {isPropertyContentLoading ? (
            <div className="carousel-slide-skeleton" aria-hidden />
          ) : carouselSlides.length > 0 ? (
            <>
              <div
                className="carousel-wrapper"
                style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              >
                {carouselSlides.map((imgSrc, index) => (
                  <div key={index} className="carousel-slide">
                    {imgSrc ? (
                      <img src={imgSrc} alt={`${property?.name || 'Property'} ${index + 1}`} loading={index === 0 ? 'eager' : 'lazy'} />
                    ) : (
                      <div className="carousel-slide-skeleton" aria-hidden />
                    )}
                  </div>
                ))}
              </div>

              {carouselSlides.length > 1 && (
                <>
                  <button
                    type="button"
                    className="carousel-nav carousel-prev"
                    onClick={goPrev}
                    aria-label="Previous image"
                  >
                    <MdChevronLeft />
                  </button>
                  <button
                    type="button"
                    className="carousel-nav carousel-next"
                    onClick={goNext}
                    aria-label="Next image"
                  >
                    <MdChevronRight />
                  </button>
                </>
              )}

              <div className="carousel-indicators">
                {carouselSlides.map((_, index) => (
                  <div
                    key={index}
                    className={`indicator ${currentImageIndex === index ? 'active' : ''}`}
                    onClick={() => setCurrentImageIndex(index)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Go to image ${index + 1}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setCurrentImageIndex(index);
                    }}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="carousel-empty">
              <MdOutlinePhoto />
            </div>
          )}
        </div>
      </div>

      {/* Responsive content layout: mobile = overview then units (DOM order); laptop = units left, overview right (CSS order) */}
      <div className="property-details-layout">
        <section className="property-left">
          <div className="overview-card">
            {loading && (
              <div className="property-loading-skeleton" aria-label="Loading property">
                <div className="pls-line pls-shimmer title" />
                <div className="pls-line pls-shimmer" />
                <div className="pls-line pls-shimmer short" />
                <div className="pls-row">
                  <div className="pls-pill pls-shimmer" />
                  <div className="pls-pill pls-shimmer" />
                  <div className="pls-pill pls-shimmer" />
                </div>
              </div>
            )}
            {error && !loading && (
              <div className="error-banner">{error}</div>
            )}

            {!loading && !error && (
              <>
                <div className="overview-header">
                  <div className="property-type-badge property-type-badge--withIcon">
                    {property?.unitCount ? <MdApartment /> : <MdHomeWork />}
                    <span>{property?.unitCount ? 'Apartments' : 'Property'}</span>
                  </div>
                </div>

                <div className="overview-title-row">
                  <h2 className="property-title">{property?.name || 'Property'}</h2>
                  <div className="property-rating-actions">
                    {propertyRatingCount > 0 && (
                      <div className="listing-stars property-detail-stars" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#febb02', marginTop: '8px' }}>
                        <div className="star-rating-display" style={{ display: 'flex' }}>
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              style={{ fontSize: '1.2rem', color: star <= propertyAverageScore ? '#febb02' : '#e2e8f0' }}
                            />
                          ))}
                        </div>
                        <span className="rating-count" style={{ fontSize: '1.1rem', color: '#666', fontWeight: '600', marginLeft: '4px' }}>
                          {propertyAverageScore.toFixed(1)}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="property-ratings-btn"
                      onClick={handleOpenRatingsModal}
                    >
                      View ratings & comments
                    </button>
                  </div>
                </div>
                
                <div className="overview-meta-row">
                  <div className="overview-location-row">
                    <MdLocationOn className="location-icon" />
                    <span className="property-location">{locationLabel}</span>
                  </div>
                  {!isBnbProperty && availableUnits.length > 0 ? (
                    <div
                      className="overview-vacant-pill"
                      aria-label={`${vacantUnitCount} vacant ${vacantUnitCount === 1 ? 'unit' : 'units'}`}
                    >
                      <span className="overview-vacant-value">{vacantUnitCount}</span>
                      <span className="overview-vacant-label">vacant</span>
                    </div>
                  ) : null}
                </div>

                {Array.isArray(property?.amenities) && property.amenities.length > 0 && (
                  <div className="amenities-row">
                    {property.amenities.slice(0, 10).map((a) => (
                      <span key={a} className="amenity-chip">
                        {a === 'parking' ? <MdLocalParking /> : <MdCheckCircle />}
                        <span>{a}</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {!loading && !error && (
            <>
              <div className="description-card">
                <h3 className="section-title">About</h3>
                <p className="description-text">{property?.description || 'No description available.'}</p>
                <div className="about-actions">
                  <button type="button" className="about-action" onClick={() => ensureAuth() && openMap()}>
                    <MdLocationOn />
                    <span>Open map</span>
                  </button>
                  <button type="button" className="about-action" onClick={() => ensureAuth() && call()} disabled={!contactPhone && token}>
                    <MdCall />
                    <span>Call</span>
                  </button>
                  <button type="button" className="about-action" onClick={() => ensureAuth() && handleStartPropertyChat()} disabled={requestLoading}>
                    <MdChatBubbleOutline />
                    <span>Message</span>
                  </button>
                  <button type="button" className="about-action" onClick={() => ensureAuth() && email()} disabled={!contactEmail && token}>
                    <MdEmail />
                    <span>Email</span>
                  </button>
                  <button type="button" className="about-action" onClick={handleShareProperty}>
                    <MdShare />
                    <span>{propertyShareFeedback || 'Share'}</span>
                  </button>
                  <button type="button" className="about-action report-btn" onClick={() => ensureAuth() && setIsReportModalOpen(true)}>
                    <MdFlag />
                    <span>Report</span>
                  </button>
                </div>
                {reportSuccessMessage && (
                  <div className="report-success-banner">
                    {reportSuccessMessage}
                  </div>
                )}
              </div>
            </>
          )}
          
          <ReportModal
            isOpen={isReportModalOpen}
            onClose={() => setIsReportModalOpen(false)}
            propertyId={propertyId}
            propertyName={property?.name}
            onReportSuccess={(msg) => {
              setReportSuccessMessage(msg);
              setTimeout(() => setReportSuccessMessage(''), 5000);
            }}
          />

          {!loading && !error && property && (
            <div ref={mapSectionRef} className="property-map-section">
              <PropertyMap
                destination={coords}
                label={property?.name || locationLabel}
                address={property?.address || locationLabel}
                height={340}
                isAuthenticated={Boolean(token)}
                onRequireAuth={() => ensureAuth()}
              />
            </div>
          )}
        </section>

        <section className="property-right">
          <div className="units-card">
            <div className="units-header">
              <h3 className="section-title">Units</h3>
              {!loading && !error && (
                <div className="units-count">
                  {visibleUnits.length} {visibleUnits.length === 1 ? 'unit' : 'units'}
                </div>
              )}
            </div>

            {!loading && !error && (
              <div className="units-toolbar" aria-label="Unit filters">
                <div className="units-toolbar-left">
                  <div className="toolbar-pill">
                    <MdTune />
                    <span>Filter</span>
                  </div>
                </div>
                <div className="units-toolbar-controls">
                  {!isBnbProperty && (
                    <label className="toolbar-field">
                      <span className="toolbar-label">Availability</span>
                      <select
                        value={availabilityFilter}
                        onChange={(e) => setAvailabilityFilter(e.target.value)}
                        className="toolbar-select"
                      >
                        <option value="all">All</option>
                        <option value="available">Available only</option>
                      </select>
                    </label>
                  )}
                  <label className="toolbar-field">
                    <span className="toolbar-label">Bedrooms</span>
                    <select
                      value={bedroomsFilter}
                      onChange={(e) => setBedroomsFilter(e.target.value)}
                      className="toolbar-select"
                    >
                      <option value="any">Any</option>
                      <option value="0">Studio</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4+</option>
                    </select>
                  </label>
                  <label className="toolbar-field">
                    <span className="toolbar-label">Sort</span>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="toolbar-select"
                    >
                      <option value="rentAsc">Rent: low to high</option>
                      <option value="rentDesc">Rent: high to low</option>
                      <option value="bedsDesc">Bedrooms</option>
                      <option value="sizeDesc">Size</option>
                    </select>
                  </label>
                </div>
              </div>
            )}

            {loading ? (
              <div className="units-empty">Loading units…</div>
            ) : error ? (
              <div className="units-empty">Couldn&apos;t load units.</div>
            ) : visibleUnits.length === 0 ? (
              <div className="units-empty">No public units available.</div>
            ) : (
              <div className="units-grid">
                {visibleUnits.map((unit) => {
                  const cover = unitCoverSrcById[unit.id] || null;
                  const hasCover = Boolean(getCoverImageForUnit(unit));
                  const isCoverLoading = Boolean(unitCoverLoadingById[unit.id]);
                  const hasCoverError = Boolean(unitCoverErrorById[unit.id]);
                  const shouldShowImage = Boolean(cover) && !hasCoverError;
                  const isSelected = unit.id === selectedUnitId;
                  return (
                    <button
                      key={unit.id}
                      type="button"
                      className={`unit-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleOpenUnit(unit.id)}
                    >
                      <div className="unit-cover">
                        {shouldShowImage ? (
                          <img
                            src={cover}
                            alt={unit.name || 'Unit'}
                            loading="lazy"
                            onError={() => {
                              setUnitCoverErrorById((prev) => ({ ...prev, [unit.id]: true }));
                            }}
                          />
                        ) : hasCover && isCoverLoading ? (
                          <div className="unit-cover-skeleton" aria-hidden />
                        ) : (
                          <div className="unit-cover-placeholder">
                            <MdOutlinePhoto />
                          </div>
                        )}
                      </div>

                      <div className="unit-body">
                        <div className="unit-title-row">
                          <div className="unit-title">{unit.name || unit.unitNumber || 'Unit'}</div>
                          {!isBnbUnit(unit) && (
                            <span className={`unit-badge ${isUnitBookable(unit) ? 'vacant' : 'occupied'}`}>
                              {isUnitBookable(unit) ? 'Vacant' : 'Occupied'}
                            </span>
                          )}
                        </div>

                        <div className="unit-meta">
                          {typeof unit.bedrooms === 'number' && (
                            <span className="unit-meta-chip">
                              <MdBed /> {unit.bedrooms} bed
                            </span>
                          )}
                          {typeof unit.bathrooms === 'number' && (
                            <span className="unit-meta-chip">
                              <MdBathtub /> {unit.bathrooms} bath
                            </span>
                          )}
                          {typeof unit.sizeSqm === 'number' && (
                            <span className="unit-meta-chip">
                              <MdStraighten /> {unit.sizeSqm} sqm
                            </span>
                          )}
                        </div>

                        <div className="unit-price-footer">
                          {isBnbUnit(unit)
                            ? (formatMoneyKES(unit.pricePerNight) ? `Price: ${formatMoneyKES(unit.pricePerNight)}/night` : '')
                            : (formatMoneyKES(unit.monthlyRent) ? `Rent: ${formatMoneyKES(unit.monthlyRent)}` : '')}
                        </div>

                        {typeof unit.depositAmount === 'number' && (
                          <div className="unit-sub">
                            Deposit: {formatMoneyKES(unit.depositAmount)}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {isRatingsModalOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="property-ratings-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Property ratings and comments"
          onClick={() => setIsRatingsModalOpen(false)}
        >
          <div className="property-ratings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="property-ratings-modal__header">
              <div>
                <h3 className="property-ratings-modal__title">Ratings & comments</h3>
                <div className="property-ratings-modal__summary">
                  <span className="property-ratings-modal__score">{modalAverageScore.toFixed(1)}</span>
                  <span className="property-ratings-modal__count">({modalRatingCount} ratings)</span>
                </div>
              </div>
              <button
                type="button"
                className="property-ratings-modal__close"
                onClick={() => setIsRatingsModalOpen(false)}
                aria-label="Close ratings modal"
              >
                <MdClose />
              </button>
            </div>

            <div className="property-ratings-modal__body">
              {ratingsLoading ? (
                <div className="property-ratings-modal__state">Loading ratings...</div>
              ) : ratingsError ? (
                <div className="property-ratings-modal__state property-ratings-modal__state--error">{ratingsError}</div>
              ) : propertyRatingsList.length === 0 ? (
                <div className="property-ratings-modal__state">No ratings yet for this property.</div>
              ) : (
                <div className="property-ratings-modal__list">
                  {propertyRatingsList.map((entry) => {
                    const fullName = [entry?.firstName, entry?.lastName].filter(Boolean).join(' ').trim() || 'Anonymous';
                    const score = parseRatingValue(entry);
                    const message = String(entry?.message || '').trim();
                    return (
                      <article key={entry?.id || `${fullName}-${entry?.createdAt || ''}`} className="property-ratings-modal__item">
                        <div className="property-ratings-modal__item-top">
                          <div className="property-ratings-modal__name">{fullName}</div>
                          <div className="property-ratings-modal__date">{formatRatingDate(entry?.createdAt)}</div>
                        </div>
                        <div className="property-ratings-modal__stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar
                              key={star}
                              className={`property-ratings-modal__star ${star <= score ? 'active' : 'empty'}`}
                            />
                          ))}
                          <span className="property-ratings-modal__item-score">{score.toFixed(1)}</span>
                        </div>
                        <p className="property-ratings-modal__comment">
                          {message || 'No written comment provided.'}
                        </p>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {selectedUnitMerged && isDrawerOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="unit-drawer-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Unit details"
          onClick={handleCloseUnitDrawer}
        >
          <div className="unit-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="unit-drawer-header">
              <div className="unit-drawer-title">
                <div className="unit-drawer-name-row">
                  <div className="unit-drawer-name">{selectedUnitMerged.name || selectedUnitMerged.unitNumber || 'Unit details'}</div>
                  {isBnbUnit(selectedUnitMerged) && isSelectedStayUnavailable && !bnbDetailsLoading && (
                    <span className="unit-drawer-unavailable-badge">Unavailable</span>
                  )}
                </div>
                <div className="unit-drawer-sub">
                  {formatMoneyKES(selectedUnitMerged.monthlyRent) ? `${formatMoneyKES(selectedUnitMerged.monthlyRent)}/mo` : ''}
                </div>
              </div>
              <div className="unit-drawer-actions">
                <button type="button" className="unit-drawer-share" onClick={handleShareUnit} aria-label="Share this unit">
                  <MdShare />
                  <span>{shareFeedback || 'Copy Link'}</span>
                </button>
                <button type="button" className="unit-drawer-close" onClick={handleCloseUnitDrawer} aria-label="Close">
                  <MdClose />
                </button>
              </div>
            </div>

            <div className="unit-drawer-body">
              {selectedUnitImageSrcs.length > 0 && (
                <div className="unit-drawer-carousel">
                  <div
                    className="unit-drawer-carousel-track"
                    style={{ transform: `translateX(-${selectedUnitImageIndex * 100}%)` }}
                  >
                    {selectedUnitImageSrcs.map((src, idx) => (
                      <div key={idx} className="unit-drawer-slide">
                        {src && !selectedUnitImageErrorByIndex[idx] ? (
                          <img
                            src={src}
                            alt={`${selectedUnitMerged.name || 'Unit'} ${idx + 1}`}
                            loading={idx === 0 ? 'eager' : 'lazy'}
                            onError={() => {
                              setSelectedUnitImageErrorByIndex((prev) => ({ ...prev, [idx]: true }));
                            }}
                          />
                        ) : src ? (
                          <div className="unit-drawer-slide-placeholder">
                            <MdOutlinePhoto />
                          </div>
                        ) : (
                          <div className="unit-drawer-slide-skeleton" aria-hidden />
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedUnitImageSrcs.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="unit-drawer-nav unit-drawer-prev"
                        onClick={() =>
                          setSelectedUnitImageIndex((prev) =>
                            (prev - 1 + selectedUnitImageSrcs.length) % selectedUnitImageSrcs.length
                          )
                        }
                        aria-label="Previous photo"
                      >
                        <MdChevronLeft />
                      </button>
                      <button
                        type="button"
                        className="unit-drawer-nav unit-drawer-next"
                        onClick={() =>
                          setSelectedUnitImageIndex((prev) =>
                            (prev + 1) % selectedUnitImageSrcs.length
                          )
                        }
                        aria-label="Next photo"
                      >
                        <MdChevronRight />
                      </button>
                    </>
                  )}
                  {selectedUnitImageSrcs.length > 1 && (
                    <div className="unit-drawer-dots">
                      {selectedUnitImageSrcs.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`unit-drawer-dot ${selectedUnitImageIndex === idx ? 'active' : ''}`}
                          onClick={() => setSelectedUnitImageIndex(idx)}
                          aria-label={`Go to photo ${idx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="unit-drawer-stats">
                <div className="drawer-stat">
                  <span className="drawer-stat-label">Bedrooms</span>
                  <span className="drawer-stat-value">{typeof selectedUnitMerged.bedrooms === 'number' ? selectedUnitMerged.bedrooms : '-'}</span>
                </div>
                <div className="drawer-stat">
                  <span className="drawer-stat-label">Bathrooms</span>
                  <span className="drawer-stat-value">{typeof selectedUnitMerged.bathrooms === 'number' ? selectedUnitMerged.bathrooms : '-'}</span>
                </div>
                <div className="drawer-stat">
                  <span className="drawer-stat-label">Size</span>
                  <span className="drawer-stat-value">{typeof selectedUnitMerged.sizeSqm === 'number' ? `${selectedUnitMerged.sizeSqm} sqm` : '-'}</span>
                </div>
              </div>

              <div className={`unit-drawer-grid${isBnbUnit(selectedUnitMerged) ? ' unit-drawer-grid--bnb-row' : ''}`}>
                {isBnbUnit(selectedUnitMerged) ? (
                  <>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Price / night</span>
                      <span className="drawer-kv-value">
                        {formatMoneyKES(selectedUnitMerged.pricePerNight) ? `${formatMoneyKES(selectedUnitMerged.pricePerNight)}/night` : '-'}
                      </span>
                    </div>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Unit number</span>
                      <span className="drawer-kv-value">{String(selectedUnitMerged.unitNumber || '').trim() || '-'}</span>
                    </div>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Type</span>
                      <span className="drawer-kv-value">{selectedUnitMerged.unitTypeName || '-'}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Deposit</span>
                      <span className="drawer-kv-value">{formatMoneyKES(selectedUnitMerged.depositAmount) || '-'}</span>
                    </div>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Due day</span>
                      <span className="drawer-kv-value">{typeof selectedUnitMerged.rentDueDay === 'number' ? selectedUnitMerged.rentDueDay : '-'}</span>
                    </div>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Type</span>
                      <span className="drawer-kv-value">{selectedUnitMerged.unitTypeName || '-'}</span>
                    </div>
                    <div className="drawer-kv">
                      <span className="drawer-kv-label">Availability</span>
                      <span className="drawer-kv-value">{isUnitBookable(selectedUnitMerged) ? 'Vacant' : 'Occupied'}</span>
                    </div>
                  </>
                )}
              </div>

              {selectedUnitMerged.description && (
                <div className="drawer-section">
                  <div className="drawer-section-title">Description</div>
                  <div className="drawer-section-text">{selectedUnitMerged.description}</div>
                </div>
              )}

              <div className="unit-drawer-request-section">
                {isBnbUnit(selectedUnitMerged) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label className="unit-drawer-request-notes-label" htmlFor="bnb-checkin-date">
                        Check-in date
                      </label>
                      <input
                        id="bnb-checkin-date"
                        type="date"
                        className="unit-drawer-request-notes"
                        value={bookingCheckInDate}
                        onChange={(e) => setBookingCheckInDate(e.target.value)}
                        disabled={requestLoading}
                      />
                    </div>
                    <div>
                      <label className="unit-drawer-request-notes-label" htmlFor="bnb-checkout-date">
                        Check-out date
                      </label>
                      <input
                        id="bnb-checkout-date"
                        type="date"
                        className="unit-drawer-request-notes"
                        value={bookingCheckOutDate}
                        onChange={(e) => setBookingCheckOutDate(e.target.value)}
                        disabled={requestLoading}
                      />
                    </div>
                  </div>
                )}
                {isBnbUnit(selectedUnitMerged) && isSelectedStayUnavailable && !bnbDetailsLoading && (
                  <div className="bnb-next-available-card">
                    {nextAvailableLoading ? (
                      <div className="bnb-next-available-card__message">Finding next available date…</div>
                    ) : nextAvailableDate ? (
                      <>
                        <div className="bnb-next-available-card__label">Next available date</div>
                        <div className="bnb-next-available-card__date">{formatDisplayDate(nextAvailableDate)}</div>
                        <button
                          type="button"
                          className="bnb-next-available-card__btn"
                          onClick={handleUseNextAvailableDate}
                          disabled={requestLoading || bnbAvailabilityChecking}
                        >
                          Use this date
                        </button>
                      </>
                    ) : (
                      <div className="bnb-next-available-card__message">
                        No upcoming availability found in the next 60 days.
                      </div>
                    )}
                  </div>
                )}
                {isBnbUnit(selectedUnitMerged) && (
                  <div style={{ marginTop: '16px' }}>
                    {bnbDetailsLoading && (
                      <div style={{ padding: '8px', background: '#f1f5f9', borderRadius: '4px', textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
                        Updating price details…
                      </div>
                    )}
                    {!bnbDetailsLoading && selectedUnitMerged.totalAmount != null && (
                      <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f9ff', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                        <div className="drawer-section-title" style={{ color: '#0369a1', fontSize: '15px', marginBottom: '8px' }}>Price Summary</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                          <span>{getNights(bookingCheckInDate, bookingCheckOutDate)} nights x {formatMoneyKES(selectedUnitMerged.pricePerNight)}</span>
                          <span>{formatMoneyKES(selectedUnitMerged.subtotal)}</span>
                        </div>
                        {(selectedUnitMerged.serviceFee > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                            <span>Service fee</span>
                            <span>{formatMoneyKES(selectedUnitMerged.serviceFee)}</span>
                          </div>
                        )}
                        {(selectedUnitMerged.taxAmount > 0) && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
                            <span>Tax</span>
                            <span>{formatMoneyKES(selectedUnitMerged.taxAmount)}</span>
                          </div>
                        )}
                        <hr style={{ border: 'none', borderTop: '1px dotted #bae6fd', margin: '8px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#0c4a6e' }}>
                          <span>Total Amount</span>
                          <span>{formatMoneyKES(selectedUnitMerged.totalAmount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isBnbUnit(selectedUnitMerged) && property?.paymentDetails && showBnbEvidenceStep && (
                  <div className="drawer-section" style={{ marginTop: '10px', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div className="drawer-section-title" style={{ color: '#0f172a', marginBottom: '8px' }}>Payment Instructions</div>
                    {property.paymentDetails.method && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Method:</strong> {property.paymentDetails.method}</div>}
                    {property.paymentDetails.paybillNumber && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Paybill:</strong> {property.paymentDetails.paybillNumber}</div>}
                    {property.paymentDetails.tillNumber && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Till Number:</strong> {property.paymentDetails.tillNumber}</div>}
                    {property.paymentDetails.pochiNumber && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Pochi Number:</strong> {property.paymentDetails.pochiNumber}</div>}
                    {property.paymentDetails.phoneNumber && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Phone Number:</strong> {property.paymentDetails.phoneNumber}</div>}
                    {property.paymentDetails.bankName && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Bank:</strong> {property.paymentDetails.bankName}</div>}
                    {property.paymentDetails.accountName && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Account Name:</strong> {property.paymentDetails.accountName}</div>}
                    {property.paymentDetails.accountNumber && <div style={{ fontSize: '14px', marginBottom: '4px' }}><strong>Account Number:</strong> {property.paymentDetails.accountNumber}</div>}
                    <hr style={{ border: 'none', borderTop: '1px solid #cbd5e1', margin: '12px 0' }} />
                    <div className="drawer-section-title" style={{ fontSize: '14px', marginBottom: '8px' }}>Payment Evidence</div>
                    <label className="unit-drawer-request-notes-label" htmlFor="bnb-payment-amount">
                      Amount Paid (KES)
                    </label>
                    <input
                      id="bnb-payment-amount"
                      type="number"
                      className="unit-drawer-request-notes"
                      placeholder="Enter amount paid"
                      value={bnbAmount}
                      onChange={(e) => setBnbAmount(e.target.value)}
                      disabled={requestLoading || bnbPhotoUploading}
                      style={{ marginBottom: '10px' }}
                    />
                    <label className="unit-drawer-request-notes-label" htmlFor="bnb-mpesa-message">
                      M-Pesa Message
                    </label>
                    <textarea
                      id="bnb-mpesa-message"
                      className="unit-drawer-request-notes"
                      placeholder="Paste your M-Pesa transaction SMS here..."
                      value={bnbMpesaMessage}
                      onChange={(e) => setBnbMpesaMessage(e.target.value)}
                      rows={2}
                      disabled={requestLoading || bnbPhotoUploading}
                      style={{ marginBottom: '10px' }}
                    />
                    <label className="unit-drawer-request-notes-label" htmlFor="bnb-photo-upload">
                      Upload Receipt Photo
                    </label>
                    <input
                      id="bnb-photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={uploadPhoto}
                      disabled={requestLoading || bnbPhotoUploading}
                      style={{ fontSize: '14px', padding: '6px 0' }}
                    />
                    {bnbPhotoUploading && <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>Uploading photo...</div>}
                    {bnbPhotoUrl && (
                      <div style={{ fontSize: '13px', color: '#10b981', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <MdCheckCircle /> Photo uploaded successfully
                      </div>
                    )}
                    {bnbPhotoUploadError && <div className="unit-drawer-request-error" role="alert" style={{ marginTop: '8px' }}>{bnbPhotoUploadError}</div>}
                  </div>
                )}
                {token && (
                  <>
                    <label className="unit-drawer-request-notes-label" htmlFor="unit-request-notes" style={{ marginTop: isBnbUnit(selectedUnitMerged) ? '16px' : '0' }}>
                      Notes (optional)
                    </label>
                    <textarea
                      id="unit-request-notes"
                      className="unit-drawer-request-notes"
                      placeholder={isBnbUnit(selectedUnitMerged) ? 'e.g. Late arrival' : 'e.g. I want to move in next month'}
                      value={requestNotes}
                      onChange={(e) => setRequestNotes(e.target.value)}
                      rows={2}
                      disabled={requestLoading}
                    />
                  </>
                )}
                {requestError && (
                  <div className="unit-drawer-request-error" role="alert">
                    {requestError}
                  </div>
                )}
                {requestSuccess && (
                  <div className="unit-drawer-request-success" role="status">
                    {isBnbUnit(selectedUnitMerged) ? 'Booking request submitted successfully.' : 'Unit requested successfully.'}
                  </div>
                )}
                <button
                  type="button"
                  className="unit-drawer-request-btn"
                  onClick={() => {
                    if (!ensureAuth()) return;
                    isBnbUnit(selectedUnitMerged) ? handleCreateBnbBooking() : handleRequestUnit();
                  }}
                  disabled={
                    token && (
                      requestLoading ||
                      bnbAvailabilityChecking ||
                      (isBnbUnit(selectedUnitMerged)
                        ? !(bookingCheckInDate && bookingCheckOutDate) ||
                        bookingCheckOutDate <= bookingCheckInDate
                        : !isUnitBookable(selectedUnitMerged))
                    )
                  }
                >
                  {!token 
                    ? (isBnbUnit(selectedUnitMerged) ? 'Login to Book' : 'Login to Request')
                    : bnbAvailabilityChecking
                    ? 'Checking availability...'
                    : requestLoading
                    ? (isBnbUnit(selectedUnitMerged) ? 'Submitting...' : 'Requesting...')
                    : (isBnbUnit(selectedUnitMerged)
                      ? (showBnbEvidenceStep ? 'Submit Booking Request' : 'Check availability')
                      : (() => {
                        const hasActiveRequest = myRequests.some(req => {
                          const unitId = req.unitId || req.unitID || req.unit?.id || req.propertyUnitId;
                          const activeStatus = ['REQUESTED', 'PENDING', 'APPROVED', 'CONFIRMED', 'ASSIGNED'];
                          return String(unitId) === String(selectedUnitMerged.id) && activeStatus.includes(String(req.status || '').toUpperCase());
                        });
                        if (hasActiveRequest) return 'Already requested';
                        return isUnitBookable(selectedUnitMerged) ? 'Request unit' : 'Unavailable';
                      })())}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PropertyDetails;
